"use client";

import Link from "next/link";
import Image from "next/image";
import { useSession, signOut } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { PlusCircle } from "lucide-react";
import type { SubscriptionLimits } from "@/lib/subscription-limits";
import { clientLogger } from "@/lib/client-logger";
import { useWhitelabelVisibility } from "@/hooks/useWhitelabelVisibility";

interface CampaignListItem {
  id: string;
  status: string;
}

/** Custom navigation menu item */
export interface NavMenuItem {
  id: string;
  type: 'TENANT_PAGE' | 'CAMPAIGN' | 'CUSTOM';
  label: string;
  customUrl?: string | null;
  openInNewTab: boolean;
  campaignSlug?: string | null;
}

/**
 * Props for the Navigation component
 */
export interface NavigationProps {
  /** Tenant configuration for whitelabel mode */
  tenant?: {
    name?: string;
    slug?: string;
    logoUrl?: string | null;
    textColor?: string | null;
    primaryColor?: string | null;
    // Header customization
    headerBackgroundColor?: string | null;
    headerTextColor?: string | null;
    headerLinkColor?: string | null;
    headerSticky?: boolean;
    headerTransparentInitial?: boolean;
    headerTransparentLogoUrl?: string | null;
    headerScrolledBackgroundColor?: string | null;
    headerScrolledTextColor?: string | null;
    // Header button colors
    headerButtonBackgroundColor?: string | null;
    headerButtonTextColor?: string | null;
    headerScrolledButtonBackgroundColor?: string | null;
    headerScrolledButtonTextColor?: string | null;
    // Custom navigation menu items
    navMenuItems?: NavMenuItem[];
  } | null;
}

/**
 * Navigation component with whitelabel support
 *
 * In SaaS mode (whitelabel disabled):
 * - Shows standard SaaS branding
 * - Shows Create Hunt button for all users
 * - Shows Campaigns and Digital Assets links
 *
 * In whitelabel mode:
 * - Shows tenant's logo if available
 * - Hides Create Hunt button for non-admin users
 * - Hides Campaigns link for non-admin users (they only see hunts they participate in)
 */
export function Navigation({ tenant }: NavigationProps = {}) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [subscriptionLimits, setSubscriptionLimits] = useState<SubscriptionLimits | null>(null);
  const [activeCampaigns, setActiveCampaigns] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  // Get user role for visibility logic
  const userRole = session?.user?.tenants?.[0]?.role;
  const visibility = useWhitelabelVisibility(userRole);
  const { showCreateHunt, showMyCampaigns: _showMyCampaigns, showBrowseHunts, showHowItWorks, isWhitelabelMode } = visibility;

  // Check if we're on the homepage (root only)
  // Only the root "/" gets transparent initial treatment, not tenant pages
  const isOnHomepage = pathname === '/';

  // Fetch subscription limits if user is logged in and subscriptions are visible
  useEffect(() => {
    const fetchLimits = async () => {
      if (!session?.user || !showCreateHunt) return;

      try {
        // Get subscription info
        const subResponse = await fetch('/api/subscription/status');
        if (subResponse.ok) {
          const subData = await subResponse.json();
          setSubscriptionLimits(subData.subscription?.limits);
        }

        // Get active campaigns count
        const campaignsResponse = await fetch('/api/campaigns');
        if (campaignsResponse.ok) {
          const campaignsData = await campaignsResponse.json();
          const activeCount = campaignsData.campaigns?.filter((c: CampaignListItem) =>
            c.status === 'PUBLISHED' || c.status === 'ACTIVE'
          ).length || 0;
          setActiveCampaigns(activeCount);
        }
      } catch (error) {
        clientLogger.error("Error fetching limits", { error });
      }
    };

    fetchLimits();
  }, [session, showCreateHunt]);

  // Scroll detection for transparent initial header
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCreateHunt = () => {
    // If not logged in, redirect to signup
    if (!session) {
      router.push('/auth/signup');
      return;
    }

    // Check if user has hit their campaign limit
    if (subscriptionLimits && subscriptionLimits.activeCampaigns !== null && subscriptionLimits.activeCampaigns !== -1) {
      if (activeCampaigns >= subscriptionLimits.activeCampaigns) {
        // Redirect to upgrade page
        router.push(`/dashboard/upgrade?message=${encodeURIComponent('You have reached your campaign limit. Upgrade to create more campaigns.')}&current=${activeCampaigns}&limit=${subscriptionLimits.activeCampaigns}&returnUrl=/dashboard/campaigns/new`);
        return;
      }
    }

    // User is logged in and has capacity, go to campaign creation
    router.push('/dashboard/campaigns/new');
  };

  // Determine brand display
  const brandName = isWhitelabelMode && tenant?.name ? tenant.name : 'Scavenger Hunt SaaS';

  // Header settings with defaults
  const headerSticky = tenant?.headerSticky ?? false;
  const headerTransparentInitial = tenant?.headerTransparentInitial ?? false;
  const headerBackgroundColor = tenant?.headerBackgroundColor || '#FFFFFF';
  const headerTextColor = tenant?.headerTextColor || '#111827';
  const headerLinkColor = tenant?.headerLinkColor || '#3B82F6';
  const headerScrolledBackgroundColor = tenant?.headerScrolledBackgroundColor || '#FFFFFF';
  const headerScrolledTextColor = tenant?.headerScrolledTextColor || '#111827';
  // Button colors
  const headerButtonBackgroundColor = tenant?.headerButtonBackgroundColor || '#3B82F6';
  const headerButtonTextColor = tenant?.headerButtonTextColor || '#FFFFFF';
  const headerScrolledButtonBackgroundColor = tenant?.headerScrolledButtonBackgroundColor || '#3B82F6';
  const headerScrolledButtonTextColor = tenant?.headerScrolledButtonTextColor || '#FFFFFF';

  // Determine current header colors based on scroll state (for transparent initial mode)
  // Transparent state only applies on homepage when enabled and not scrolled
  const isInTransparentState = headerTransparentInitial && !isScrolled && isOnHomepage;

  // Determine logo based on transparent state
  const defaultLogoUrl = isWhitelabelMode && tenant?.logoUrl ? tenant.logoUrl : null;
  const transparentLogoUrl = isWhitelabelMode && tenant?.headerTransparentLogoUrl ? tenant.headerTransparentLogoUrl : null;
  // Use transparent logo when in transparent state, fall back to default logo
  const brandLogoUrl = isInTransparentState && transparentLogoUrl ? transparentLogoUrl : defaultLogoUrl;

  // Use scrolled colors whenever we're NOT in transparent state
  // This includes: non-homepage pages, homepage when scrolled, homepage when transparent disabled
  const shouldUseScrolledColors = !isInTransparentState;

  const currentBackgroundColor = isInTransparentState
    ? 'transparent'
    : (shouldUseScrolledColors ? headerScrolledBackgroundColor : headerBackgroundColor);
  const currentTextColor = isInTransparentState
    ? headerTextColor
    : (shouldUseScrolledColors ? headerScrolledTextColor : headerTextColor);
  const currentLinkColor = isInTransparentState
    ? headerLinkColor
    : (shouldUseScrolledColors ? headerScrolledTextColor : headerLinkColor);
  const currentButtonBackgroundColor = isInTransparentState
    ? headerButtonBackgroundColor
    : (shouldUseScrolledColors ? headerScrolledButtonBackgroundColor : headerButtonBackgroundColor);
  const currentButtonTextColor = isInTransparentState
    ? headerButtonTextColor
    : (shouldUseScrolledColors ? headerScrolledButtonTextColor : headerButtonTextColor);

  // Use current text color for navigation text
  const navTextColor = isWhitelabelMode ? currentTextColor : (tenant?.textColor || null);

  // Helper function for link text colors
  const getLinkClasses = () => {
    if (isWhitelabelMode) {
      return 'inline-flex items-center px-1 pt-1 text-sm font-medium opacity-80 hover:opacity-100';
    }
    return `${
      isOnHomepage
        ? 'text-white/80 hover:text-white'
        : 'text-gray-500 hover:text-gray-700'
    } inline-flex items-center px-1 pt-1 text-sm font-medium`;
  };

  const getLinkStyle = () => {
    if (isWhitelabelMode) {
      return {
        color: currentLinkColor,
        transition: 'color 0.3s ease',
      };
    }
    return undefined;
  };

  // Get text style (for non-link text elements)
  const getTextStyle = () => {
    if (isWhitelabelMode) {
      return {
        color: navTextColor || '#111827',
        transition: 'color 0.3s ease',
      };
    }
    return undefined;
  };

  // Get button style for sign in/out buttons
  const getButtonStyle = (): React.CSSProperties | undefined => {
    if (isWhitelabelMode) {
      return {
        backgroundColor: currentButtonBackgroundColor,
        color: currentButtonTextColor,
        borderColor: currentButtonBackgroundColor,
        transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
      };
    }
    return undefined;
  };

  // Get custom menu items from tenant
  const customMenuItems = tenant?.navMenuItems || [];

  // Helper function to get the URL for a menu item
  const getMenuItemUrl = (item: NavMenuItem): string => {
    switch (item.type) {
      case 'TENANT_PAGE':
        return tenant?.slug ? `/${tenant.slug}` : '/';
      case 'CAMPAIGN':
        return item.campaignSlug && tenant?.slug ? `/${tenant.slug}/campaigns/${item.campaignSlug}` : '/';
      case 'CUSTOM':
        return item.customUrl || '/';
      default:
        return '/';
    }
  };

  // Render a single custom menu item
  const renderCustomMenuItem = (item: NavMenuItem, isMobile = false) => {
    const url = getMenuItemUrl(item);
    const isExternal = item.type === 'CUSTOM' && item.customUrl &&
      (item.customUrl.startsWith('http://') || item.customUrl.startsWith('https://'));

    if (isMobile) {
      return (
        <Link
          key={item.id}
          href={url}
          className="block px-3 py-2 text-base font-medium"
          style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
          onClick={() => setIsMobileMenuOpen(false)}
          {...(isExternal && item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <Link
        key={item.id}
        href={url}
        className={getLinkClasses()}
        style={getLinkStyle()}
        {...(isExternal && item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {item.label}
      </Link>
    );
  };

  // Build nav classes based on mode and settings
  const getNavClasses = () => {
    const classes: string[] = [];

    if (isWhitelabelMode) {
      // Whitelabel mode - use tenant's header settings
      if (headerSticky) {
        classes.push('fixed top-0 left-0 right-0 z-50');
      }
      if (!isInTransparentState) {
        classes.push('shadow-sm');
      }
    } else {
      // SaaS mode - original behavior
      if (isOnHomepage) {
        classes.push('bg-transparent absolute top-0 left-0 right-0 z-50');
      } else {
        classes.push('bg-white shadow-sm border-b');
      }
    }

    return classes.join(' ');
  };

  // Build nav style for whitelabel
  const getNavStyle = (): React.CSSProperties | undefined => {
    if (!isWhitelabelMode) return undefined;

    return {
      backgroundColor: currentBackgroundColor,
      transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    };
  };

  return (
    <nav
      className={getNavClasses()}
      style={getNavStyle()}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              href="/"
              className="flex items-center"
              style={getTextStyle()}
            >
              {brandLogoUrl ? (
                <Image
                  src={brandLogoUrl}
                  alt={brandName}
                  width={150}
                  height={40}
                  className="h-10 w-auto"
                  unoptimized
                />
              ) : (
                <span className="text-xl font-bold">{brandName}</span>
              )}
            </Link>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {/* Custom menu items from tenant settings - shown first */}
              {isWhitelabelMode && customMenuItems.map(item => renderCustomMenuItem(item))}
              {session ? (
                // Authenticated user navigation - order: My Assets, Dashboard, Settings
                <>
                  <Link href="/my-assets" className={getLinkClasses()} style={getLinkStyle()}>
                    My Assets
                  </Link>
                  <Link href="/dashboard" className={getLinkClasses()} style={getLinkStyle()}>
                    Dashboard
                  </Link>
                  {showBrowseHunts && (
                    <Link href="/browse" className={getLinkClasses()} style={getLinkStyle()}>
                      Browse Hunts
                    </Link>
                  )}
                  <Link href="/dashboard/settings" className={getLinkClasses()} style={getLinkStyle()}>
                    Settings
                  </Link>
                </>
              ) : (
                // Public navigation
                <>
                  {showBrowseHunts && (
                    <Link href="/browse" className={getLinkClasses()} style={getLinkStyle()}>
                      Browse Hunts
                    </Link>
                  )}
                  {showHowItWorks && (
                    <Link href="/how-it-works" className={getLinkClasses()} style={getLinkStyle()}>
                      How it Works
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            {/* Create Hunt button - hidden in whitelabel mode (use dashboard instead) */}
            {showCreateHunt && !isWhitelabelMode && (
              <Button
                onClick={handleCreateHunt}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create Hunt
              </Button>
            )}

            {status === "loading" ? (
              <div className="animate-pulse">
                <div className="h-4 w-20 bg-gray-200 rounded"></div>
              </div>
            ) : session ? (
              // Authenticated user
              <>
                <span
                  className="text-sm"
                  style={getTextStyle()}
                >
                  {session.user.firstName} {session.user.lastName}
                </span>
                {session.user.tenants && session.user.tenants.length > 0 && (
                  <span
                    className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700"
                  >
                    {session.user.tenants[0].name}
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await signOut({ redirect: false });
                    window.location.href = "/";
                  }}
                  style={getButtonStyle()}
                >
                  Sign out
                </Button>
              </>
            ) : (
              // Unauthenticated user
              <>
                <Link
                  href="/auth/signin"
                  className="text-sm font-medium px-4 py-2 rounded opacity-90 hover:opacity-100"
                  style={getButtonStyle()}
                >
                  Sign In
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
                isWhitelabelMode ? '' : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
              }`}
              style={isWhitelabelMode ? {
                color: isInTransparentState ? headerTextColor : headerScrolledTextColor,
              } : undefined}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="sm:hidden"
            style={isWhitelabelMode ? {
              backgroundColor: headerScrolledBackgroundColor,
            } : undefined}
          >
            {/* Create Hunt button for mobile - hidden in whitelabel mode (use dashboard instead) */}
            {showCreateHunt && !isWhitelabelMode && (
              <div className="px-3 pt-2 pb-3">
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleCreateHunt();
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create Hunt
                </Button>
              </div>
            )}

            <div
              className="pt-2 pb-3 space-y-1 border-t"
              style={isWhitelabelMode ? {
                borderColor: `${headerScrolledTextColor}20`,
              } : { borderColor: '#e5e7eb' }}
            >
              {/* Custom menu items from tenant settings - shown first */}
              {isWhitelabelMode && customMenuItems.map(item => renderCustomMenuItem(item, true))}
              {session ? (
                // Authenticated user mobile navigation - order: My Assets, Dashboard, Settings
                <>
                  <Link
                    href="/my-assets"
                    className="block px-3 py-2 text-base font-medium"
                    style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Assets
                  </Link>
                  <Link
                    href="/dashboard"
                    className="block px-3 py-2 text-base font-medium"
                    style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {showBrowseHunts && (
                    <Link
                      href="/browse"
                      className="block px-3 py-2 text-base font-medium"
                      style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Browse Hunts
                    </Link>
                  )}
                  <Link
                    href="/dashboard/settings"
                    className="block px-3 py-2 text-base font-medium"
                    style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                </>
              ) : (
                // Public mobile navigation
                <>
                  {showBrowseHunts && (
                    <Link
                      href="/browse"
                      className="block px-3 py-2 text-base font-medium"
                      style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Browse Hunts
                    </Link>
                  )}
                  {showHowItWorks && (
                    <Link
                      href="/how-it-works"
                      className="block px-3 py-2 text-base font-medium"
                      style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      How it Works
                    </Link>
                  )}
                </>
              )}
            </div>

            <div
              className="pt-4 pb-3 border-t"
              style={isWhitelabelMode ? {
                borderColor: `${headerScrolledTextColor}20`,
              } : { borderColor: '#e5e7eb' }}
            >
              {session ? (
                <div className="space-y-1">
                  <div className="px-3 py-2">
                    <div
                      className="text-base font-medium"
                      style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#1f2937' }}
                    >
                      {session.user.firstName} {session.user.lastName}
                    </div>
                    <div
                      className="text-sm"
                      style={isWhitelabelMode ? { color: `${headerScrolledTextColor}99` } : { color: '#6b7280' }}
                    >
                      {session.user.email}
                    </div>
                    {session.user.tenants && session.user.tenants.length > 0 && (
                      <div
                        className="text-xs px-2 py-1 rounded mt-1 inline-block"
                        style={isWhitelabelMode ? {
                          backgroundColor: `${headerScrolledTextColor}15`,
                          color: headerScrolledTextColor
                        } : {
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280'
                        }}
                      >
                        {session.user.tenants[0].name}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      setIsMobileMenuOpen(false);
                      await signOut({ redirect: false });
                      window.location.href = "/";
                    }}
                    className="block w-full text-left px-3 py-2 text-base font-medium"
                    style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                  >
                    Sign out
                  </button>
                </div>
              ) : (
                <div className="space-y-1">
                  <Link
                    href="/auth/signin"
                    className="block px-3 py-2 text-base font-medium"
                    style={isWhitelabelMode ? { color: headerScrolledTextColor } : { color: '#6b7280' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/auth/signup"
                    className="block px-3 py-2 text-base font-medium rounded-md mx-3"
                    style={isWhitelabelMode ? {
                      backgroundColor: headerScrolledButtonBackgroundColor,
                      color: headerScrolledButtonTextColor
                    } : {
                      backgroundColor: '#2563eb',
                      color: '#ffffff'
                    }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
